import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { barFlex, buildTicks, fullLabel, tickAlign } from './chartAxis.js';

const CHART_H = 108;

// Distinct palette. "Other" (the backend's roll-up row) always renders grey.
export const CATEGORY_COLORS = [
  '#007BFF', '#00C4CC', '#7E57C2', '#EF6C00', '#2E7D32',
  '#C2185B', '#0097A7', '#5D4037',
];
const OTHER_COLOR = '#9E9E9E';

export function colorFor(row, i) {
  return row.category_id == null ? OTHER_COLOR : CATEGORY_COLORS[i % CATEGORY_COLORS.length];
}

/**
 * Clustered bar chart: one cluster per date bucket, one coloured bar per category.
 *
 * `rows` are the backend's `category_series` entries — each already aligned to the shared
 * `dates` domain, so `row[metric][i]` belongs to `dates[i]`. The backend caps the number of
 * rows (top N by volume + an "Other" roll-up), which is what keeps this readable for an org
 * with many categories; the chart itself just scrolls horizontally.
 */
export default function GroupedBars({ rows, dates, bucket = 'day', metric = 'appointments', label }) {
  if (!rows || rows.length === 0 || !dates || dates.length === 0) return null;

  const max = Math.max(1, ...rows.flatMap((r) => r[metric] || []));
  const ticks = buildTicks(dates, bucket);
  // Each cluster holds one bar per category, so it needs proportionally more room.
  const flex = barFlex(bucket, rows.length);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{label}</Typography>
        <Typography variant="caption" color="text.disabled">max {max}</Typography>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 0.75, mb: 0.5 }}>
        {rows.map((r, i) => (
          <Box key={r.category_id ?? 'other'} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: colorFor(r, i), flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>{r.name}</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          height: CHART_H, pr: 0.75, flexShrink: 0 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, lineHeight: 1 }}>{max}</Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, lineHeight: 1 }}>0</Typography>
        </Box>

        <Box sx={{ overflowX: 'auto', pb: 0.5, flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: CHART_H,
            borderBottom: '1px solid', borderColor: 'divider' }}>
            {dates.map((date, i) => (
              <Box key={date} sx={{ ...flex, display: 'flex', alignItems: 'flex-end', gap: '1px' }}>
                {rows.map((r, ri) => {
                  const v = (r[metric] || [])[i] || 0;
                  return (
                    <Tooltip key={r.category_id ?? 'other'} arrow
                      title={`${fullLabel(date, bucket)} · ${r.name}: ${v}`}>
                      <Box sx={{
                        flex: '1 1 0', minWidth: 3,
                        height: `${Math.max(2, (v / max) * (CHART_H - 4))}px`,
                        bgcolor: colorFor(r, ri), borderRadius: '2px 2px 0 0',
                        opacity: v ? 1 : 0.18, cursor: 'default',
                      }} />
                    </Tooltip>
                  );
                })}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: '6px', mt: 0.5 }}>
            {dates.map((date, i) => (
              <Box key={date} sx={{ ...flex, display: 'flex', flexDirection: 'column',
                alignItems: tickAlign(i, dates.length), lineHeight: 1.15 }}>
                {(ticks[i] || []).map((line) => (
                  <Typography key={line} variant="caption" color="text.disabled"
                    sx={{ fontSize: 9, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
